import { signal } from '@angular/core';
import { PaginatedObjects, PaginationRequest } from './models/pagination.models';

/**
 * Caches a paginated collection and exposes incremental loading state.
 */
export class PaginationStore<T> {
  readonly items = signal<T[]>([]);
  readonly hasMore = signal(true);
  readonly totalItems = signal(0);
  readonly loading = signal(false);
  readonly loadFailed = signal(false);
  readonly isInitialLoad = signal(true);
  readonly initialLoad: Promise<T[]>;

  private lastPage = 0;
  private version = 0;

  constructor(
    private readonly pageSize: number,
    private readonly loadPage: (pagination: PaginationRequest) => Promise<PaginatedObjects<T>>
  ) {
    this.initialLoad = this.loadMore();
  }

  async loadMore(): Promise<T[]> {
    if (!this.hasMore() || this.loading()) return [];

    const version = this.version;
    this.loading.set(true);
    this.loadFailed.set(false);
    try {
      const result = await this.loadPage({ page: this.lastPage + 1, perPage: this.pageSize });
      if (version !== this.version) return [];

      this.lastPage = result.pagination.page;
      this.items.update(items => [...items, ...result.items]);
      this.totalItems.set(result.pagination.total);
      this.hasMore.set(result.pagination.hasNext);
      return result.items;
    } catch {
      if (version !== this.version) return [];
      this.loadFailed.set(true);
      return [];
    } finally {
      if (version === this.version) {
        this.loading.set(false);
        this.isInitialLoad.set(false);
      }
    }
  }

  refresh(): void {
    this.version++;
    this.lastPage = 0;
    this.items.set([]);
    this.totalItems.set(0);
    this.hasMore.set(true);
    this.loading.set(false);
    this.loadFailed.set(false);
    void this.loadMore();
  }
}
